import { useState, useRef, useEffect } from 'react';
import { ClipboardCheck, Plus, Camera, AlertCircle, Search, ExternalLink, Upload, FileText } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useOffline } from '../../contexts/OfflineContext';
import { useAuth } from '../../contexts/AuthContext';
import { uploadInspectionPhoto, uploadLockPhoto } from '../../utils/storage';
import { isChecklist, inspectionProgress, checklistSteps, stepAddressed, stepNotes } from '../../utils/inspectionProgress';
import { unitHasTag } from '../../utils/dataQueries';
import { coerceLockCounts } from '../../utils/unitDetailsHelpers';
import { INSPECTION_TYPES } from '../../data/inspectionTypes';
import { colors, Header, Card, PillToggle, Button, SelectField, LockFields, NotesSlideshow } from '../shared';

const InspectScreen = () => {
  const { inspections, units, addInspection, updateInspection, getCurrentTech, saveUnitDetails, getUnitDetails } = useData();
  const { isOnline, queuePhoto, canQueuePhoto } = useOffline();
  const { user } = useAuth();

  const [view, setView] = useState('list'); // 'list' | 'new' | 'form'
  const [lockValues, setLockValues] = useState({}); // lock-form step: unit_details lock fields
  const [lockPhotoFile, setLockPhotoFile] = useState(null); // lock-form step: photo of the new lock
  const [savingLock, setSavingLock] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [unitSearch, setUnitSearch] = useState('');
  const [formData, setFormData] = useState({});
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null); // photo url shown full-screen
  const [stepIndex, setStepIndex] = useState(0); // current step in the checklist step-by-step view
  const [notesOpen, setNotesOpen] = useState(false); // notes-review slideshow open state

  const fileInputRef = useRef(null);
  const currentTech = getCurrentTech();

  // Get the current tech's inspections. Match by auth uid first (techId is set
  // to user.uid at create time) — email alone misses when getCurrentTech resolves
  // a tech whose record email differs from the logged-in account.
  const myEmail = (currentTech?.email || user?.email || '').toLowerCase();
  const myInspections = inspections.filter(i =>
    (user?.uid && i.techId === user.uid) ||
    (myEmail && i.techEmail?.toLowerCase() === myEmail)
  );

  const statusStyle = {
    pending: { bg: colors.yellowDim, color: colors.yellow },
    complete: { bg: colors.greenDim, color: colors.green },
    'in-progress': { bg: colors.orangeDim, color: colors.orange },
  };

  const handleStartInspection = async (unitId, type) => {
    if (!unitId || !type) return;

    setSaving(true);
    try {
      const unit = units.find(u => u.id === unitId);
      const inspectionId = await addInspection({
        unitId,
        property: unit?.propertyName || '',
        address: unit?.address || '',
        type,
        status: 'in-progress',
        items: {},
        notes: '',
        photos: [],
      });

      setSelectedInspection({
        id: inspectionId,
        unitId,
        property: unit?.propertyName || '',
        address: unit?.address || '',
        type,
        status: 'in-progress',
        items: {},
      });
      setFormData({});
      setNotes('');
      setPhotos([]);
      setStepIndex(0);
      setView('form');
    } catch (error) {
      console.error('Error starting inspection:', error);
      alert('Failed to start inspection');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInspection = async (complete = false) => {
    if (!selectedInspection) return;

    setSaving(true);
    try {
      await updateInspection(selectedInspection.id, {
        items: formData,
        notes,
        photos,
        status: complete ? 'complete' : 'in-progress',
        completedAt: complete ? new Date().toISOString() : null,
      });

      setView('list');
      setSelectedInspection(null);
    } catch (error) {
      console.error('Error saving inspection:', error);
      alert('Failed to save inspection');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedInspection) return;

    try {
      if (isOnline) {
        const result = await uploadInspectionPhoto(
          file,
          selectedInspection.unitId,
          selectedInspection.type,
          selectedInspection.id
        );
        setPhotos(prev => [...prev, { url: result.url, path: result.path, name: file.name }]);
      } else if (canQueuePhoto) {
        const queued = queuePhoto({
          type: 'inspection',
          inspectionId: selectedInspection.id,
          file,
        });
        if (queued.success) {
          setPhotos(prev => [...prev, { name: file.name, pending: true }]);
        } else {
          alert(queued.error);
        }
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo');
    }
  };

  const updateFormItem = (itemId, value) => {
    setFormData(prev => ({ ...prev, [itemId]: value }));
  };

  // Checklist (onboarding) step handlers — each step is stored as
  // formData[stepId] = { status: 'done'|'skipped', note, photos: [] }
  // Step-by-step auto-advance: the tap SETS the status (no toggle-clear — you'd
  // never see the cleared state before the view advances).

  // Write a step's patch to local state AND the inspection doc immediately —
  // not only on Save Draft/Complete. Progress must survive leaving via the app
  // nav, and the lock step's "Save Lock Info" must actually land on the
  // inspection; otherwise the doc keeps items:{} and every report reads 0/total.
  // Writes the whole items map so any note typed before the tap rides along.
  // ponytail: one small merge-write per tap (≤8 per onboarding).
  const persistStep = (stepId, patch) => {
    const next = { ...formData, [stepId]: { ...(formData[stepId] || {}), ...patch } };
    setFormData(next);
    if (selectedInspection?.id) {
      updateInspection(selectedInspection.id, { items: next })
        .catch(err => console.error('Error auto-saving step:', err));
    }
  };
  const setStepStatus = (stepId, status) => persistStep(stepId, { status });
  const setStepNote = (stepId, note) => {
    setFormData(prev => ({ ...prev, [stepId]: { ...(prev[stepId] || {}), note } }));
  };
  const addStepPhoto = (stepId, photo) => {
    setFormData(prev => {
      const cur = prev[stepId] || {};
      return { ...prev, [stepId]: { ...cur, photos: [...(cur.photos || []), photo] } };
    });
  };
  const removeStepPhoto = (stepId, idx) => {
    setFormData(prev => {
      const cur = prev[stepId] || {};
      return { ...prev, [stepId]: { ...cur, photos: (cur.photos || []).filter((_, i) => i !== idx) } };
    });
  };
  const handleStepPhotoUpload = async (stepId, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selectedInspection) return;
    try {
      if (isOnline) {
        const result = await uploadInspectionPhoto(
          file,
          selectedInspection.unitId,
          selectedInspection.type,
          selectedInspection.id
        );
        addStepPhoto(stepId, { url: result.url, path: result.path, name: file.name });
      } else if (canQueuePhoto) {
        const queued = queuePhoto({ type: 'inspection', inspectionId: selectedInspection.id, stepId, file });
        if (queued.success) addStepPhoto(stepId, { name: file.name, pending: true });
        else alert(queued.error);
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo');
    }
  };

  // Lock-form step (onboarding "Replace Unit locks"): writes lock fields to the
  // canonical unit_details store via saveUnitDetails, then marks the step done.
  // Prefill from unit_details each time the tech lands on the step.
  useEffect(() => {
    if (view !== 'form' || !selectedInspection) return;
    const def = INSPECTION_TYPES[selectedInspection.type];
    if (!isChecklist(def)) return;
    const steps = checklistSteps(def);
    const cur = steps[Math.min(stepIndex, Math.max(0, steps.length - 1))];
    if (cur?.lockForm) {
      setLockPhotoFile(null);
      const d = getUnitDetails(selectedInspection.unitId) || {};
      setLockValues({
        unitLockCode: d.unitLockCode || '',
        unitLockType: d.unitLockType || '',
        unitLockCount: d.unitLockCount ?? '',
        unitLockNotes: d.unitLockNotes || '',
        tenantLockbox: d.tenantLockbox || '',
        commonLockCode: d.commonLockCode || '',
        commonLockType: d.commonLockType || '',
        commonLockCount: d.commonLockCount ?? '',
        commonLockNotes: d.commonLockNotes || '',
      });
    }
  }, [view, selectedInspection, stepIndex, getUnitDetails]);

  const handleSaveLock = async (stepId, isLast) => {
    if (!selectedInspection) return;
    setSavingLock(true);
    try {
      const valuesToSave = coerceLockCounts(lockValues);

      // Attach the lock photo the same way the Unit Details edit screen does, so it
      // lands on the canonical unit_details doc (photoUrl/photoPath).
      if (lockPhotoFile) {
        if (isOnline) {
          const result = await uploadLockPhoto(lockPhotoFile, selectedInspection.unitId);
          valuesToSave.photoUrl = result.url;
          valuesToSave.photoPath = result.path;
        } else if (canQueuePhoto) {
          const queued = queuePhoto({ type: 'lock', unitId: selectedInspection.unitId, file: lockPhotoFile });
          if (!queued.success) { alert(queued.error); setSavingLock(false); return; }
        }
      }
      const note = formData[stepId]?.note;
      if (note) valuesToSave.changeNotes = note;

      // saveUnitDetails diffs internally and no-ops if unchanged — either way
      // the tech confirmed the codes, so mark the step done and advance.
      // ponytail: no-op when no *tracked* field changed drops a photo/note-only save.
      // Onboarding always enters a new code → there's a diff, so it rides along. Add a
      // forced write only if photo-only saves become a real need.
      await saveUnitDetails(selectedInspection.unitId, valuesToSave);
      setStepStatus(stepId, 'done');
      setLockPhotoFile(null);
      if (!isLast) setStepIndex(i => i + 1);
    } catch (error) {
      console.error('Error saving lock info:', error);
      alert('Failed to save lock info. Please try again.');
    } finally {
      setSavingLock(false);
    }
  };

  // Inspection Form View
  if (view === 'form' && selectedInspection) {
    const inspectionDef = INSPECTION_TYPES[selectedInspection.type];
    const isCheck = isChecklist(inspectionDef);
    const { done: completedItems, total: totalItems } = inspectionProgress(inspectionDef, formData);

    // Checklist is shown one step at a time. Flatten sections→steps, keeping the
    // section title so the header still reads "GREET TENANTS" etc.
    const flatSteps = isCheck
      ? inspectionDef.sections.flatMap(sec => sec.steps.map(step => ({ ...step, sectionTitle: sec.title })))
      : [];
    const stepIdx = Math.min(stepIndex, Math.max(0, flatSteps.length - 1));
    const step = flatSteps[stepIdx];
    const entry = (step && formData[step.id]) || {};
    const isLastStep = stepIdx >= flatSteps.length - 1;

    // Steps carrying a note and/or photos — the review slideshow pages through these.
    const noteSlides = stepNotes(inspectionDef, formData);

    return (
      <div className="h-full overflow-y-auto pb-20" style={{ background: colors.black }}>
        <Header
          title={inspectionDef.label}
          subtitle={`Unit ${selectedInspection.unitId} • ${selectedInspection.address || selectedInspection.property}`}
          showBack
          onBack={() => {
            if (confirm('Save progress before leaving?')) {
              handleSaveInspection(false);
            } else {
              setView('list');
              setSelectedInspection(null);
            }
          }}
        />
        <div className="px-4">
          {/* Progress */}
          <Card className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm" style={{ color: colors.textSecondary }}>Progress</span>
              <span className="font-semibold" style={{ color: colors.yellow }}>{completedItems}/{totalItems}</span>
            </div>
            <div className="h-2 rounded-full" style={{ background: colors.inputBg }}>
              <div
                className="h-2 rounded-full transition-all"
                style={{ width: `${(completedItems / totalItems) * 100}%`, background: colors.yellow }}
              />
            </div>
          </Card>

          {/* Checklist (onboarding) — one step at a time, auto-advance on answer */}
          {isCheck && step && (
            <>
              <div className="mb-3">
                <p className="text-xs font-semibold uppercase tracking-wide mb-2 px-1" style={{ color: colors.textMuted }}>
                  {step.sectionTitle}
                </p>
                <Card>
                  <p className="font-medium" style={{ color: colors.textPrimary }}>{step.title}</p>
                  {step.detail && (
                    <p className="text-sm mt-1 mb-3" style={{ color: colors.textMuted }}>{step.detail}</p>
                  )}
                  {step.lockForm ? (
                    <div className="mb-3">
                      <LockFields values={lockValues} onChange={(f, val) => setLockValues(p => ({ ...p, [f]: val }))} />

                      {/* Change note + lock photo — saved to unit_details with the
                          codes, mirroring the Unit Details edit screen. */}
                      <label className="block text-sm font-medium mb-2 mt-1" style={{ color: colors.textSecondary }}>
                        Change Notes <span style={{ color: colors.textMuted }}>(optional)</span>
                      </label>
                      <textarea
                        value={entry.note || ''}
                        onChange={e => setStepNote(step.id, e.target.value)}
                        placeholder="Why are you making this change?"
                        rows={2}
                        className="w-full px-3 py-2 rounded-lg outline-none resize-none text-sm mb-3"
                        style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
                      />

                      <p className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                        Lock Photo <span style={{ color: colors.textMuted }}>(optional)</span>
                      </p>
                      <label
                        className="block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer"
                        style={{ borderColor: lockPhotoFile ? colors.green : colors.border, background: colors.inputBg }}
                      >
                        <Upload size={28} style={{ color: lockPhotoFile ? colors.green : colors.textMuted }} className="mx-auto mb-2" />
                        <p className="text-sm" style={{ color: lockPhotoFile ? colors.green : colors.textSecondary }}>
                          {lockPhotoFile ? lockPhotoFile.name : 'Tap to upload photo'}
                        </p>
                        <p className="text-xs" style={{ color: colors.textMuted }}>JPG/PNG/WEBP up to 8MB</p>
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          onChange={e => setLockPhotoFile(e.target.files?.[0] || null)}
                        />
                      </label>

                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => handleSaveLock(step.id, isLastStep)}
                          disabled={savingLock}
                          className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                          style={{ background: colors.yellow, color: colors.black }}
                        >
                          {savingLock ? 'Saving...' : entry.status === 'done' ? 'Update Lock Info' : 'Save Lock Info'}
                        </button>
                        <button
                          onClick={() => {
                            setStepStatus(step.id, 'skipped');
                            if (!isLastStep) setStepIndex(stepIdx + 1);
                          }}
                          className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                          style={{
                            background: entry.status === 'skipped' ? colors.orange : colors.inputBg,
                            color: entry.status === 'skipped' ? colors.black : colors.textSecondary,
                          }}
                        >
                          Skip / N-A
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 mb-3">
                      {[
                        { key: 'done', label: 'Done', on: colors.green },
                        { key: 'skipped', label: 'Skip / N-A', on: colors.orange },
                      ].map(btn => {
                        const active = entry.status === btn.key;
                        return (
                          <button
                            key={btn.key}
                            onClick={() => {
                              setStepStatus(step.id, btn.key);
                              if (!isLastStep) setStepIndex(stepIdx + 1); // auto-advance
                            }}
                            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                            style={{
                              background: active ? btn.on : colors.inputBg,
                              color: active ? colors.black : colors.textSecondary,
                            }}
                          >
                            {btn.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {step.workOrder && (
                    <a
                      href={step.workOrder.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg text-sm font-semibold"
                      style={{ background: colors.yellowDim, color: colors.yellow, border: `1px solid ${colors.yellow}` }}
                    >
                      <ExternalLink size={16} /> Create Work Order — assign to {step.workOrder.assignee}
                    </a>
                  )}
                  {!step.noNote && (
                    <textarea
                      value={entry.note || ''}
                      onChange={e => setStepNote(step.id, e.target.value)}
                      placeholder="Note (optional)"
                      rows={2}
                      className="w-full px-3 py-2 rounded-lg outline-none resize-none text-sm"
                      style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
                    />
                  )}
                  {step.photo && (
                    <div className="mt-3">
                      <p className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Photos</p>
                      <div className="flex flex-wrap gap-2">
                        {(entry.photos || []).map((photo, i) => (
                          <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden" style={{ background: colors.inputBg }}>
                            {photo.url ? (
                              <img
                                src={photo.url}
                                alt=""
                                onClick={() => setLightbox(photo.url)}
                                className="w-full h-full object-cover cursor-pointer"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: colors.yellow }}>
                                Pending
                              </div>
                            )}
                            <button
                              onClick={() => removeStepPhoto(step.id, i)}
                              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full flex items-center justify-center text-sm leading-none"
                              style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}
                              aria-label="Remove photo"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <label
                          className="w-20 h-20 rounded-lg flex flex-col items-center justify-center gap-1 border-2 border-dashed cursor-pointer"
                          style={{ borderColor: colors.border, color: colors.textMuted }}
                        >
                          <Camera size={22} />
                          <span className="text-[10px] font-semibold uppercase">Add</span>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={e => handleStepPhotoUpload(step.id, e)}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </Card>
              </div>

              {/* Review all notes/photos entered so far — opens a slideshow modal */}
              {noteSlides.length > 0 && (
                <button
                  onClick={() => setNotesOpen(true)}
                  className="flex items-center justify-center gap-2 w-full mb-3 px-4 py-2 rounded-lg text-sm font-semibold"
                  style={{ background: colors.inputBg, color: colors.textSecondary }}
                >
                  <FileText size={16} /> View all notes ({noteSlides.length})
                </button>
              )}

              {/* Step navigation — Back only; Done/Skip advance forward */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setStepIndex(stepIdx - 1)}
                  disabled={stepIdx === 0}
                  className="px-4 py-2 rounded-lg text-sm font-semibold"
                  style={{ background: colors.inputBg, color: colors.textSecondary, opacity: stepIdx === 0 ? 0.4 : 1 }}
                >
                  ◀ Back
                </button>
                <span className="text-xs" style={{ color: colors.textMuted }}>
                  Step {stepIdx + 1} of {flatSteps.length}
                </span>
              </div>
            </>
          )}

          {/* Form Items (options format) */}
          {!isCheck && inspectionDef.items.map(item => (
            <Card key={item.id} className="mb-3">
              <p className="font-medium mb-3" style={{ color: colors.textPrimary }}>{item.label}</p>
              <div className="flex flex-wrap gap-2">
                {item.options.map(opt => (
                  <button
                    key={opt}
                    onClick={() => updateFormItem(item.id, opt)}
                    className="px-3 py-2 rounded-lg text-sm font-medium transition-all"
                    style={{
                      background: formData[item.id] === opt ? colors.yellow : colors.inputBg,
                      color: formData[item.id] === opt ? colors.black : colors.textSecondary,
                    }}
                  >
                    {item.type === 'scale' ? `${opt}/5` : opt}
                  </button>
                ))}
              </div>
            </Card>
          ))}

          {/* Photos (options format — checklist uses per-step photos) */}
          {!isCheck && (
            <Card className="mb-4">
              <p className="font-medium mb-3" style={{ color: colors.textPrimary }}>Photos</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <div className="flex flex-wrap gap-2 mb-3">
                {photos.map((photo, i) => (
                  <div
                    key={i}
                    className="w-16 h-16 rounded-lg flex items-center justify-center text-xs"
                    style={{ background: photo.pending ? colors.yellowDim : colors.greenDim, color: photo.pending ? colors.yellow : colors.green }}
                  >
                    {photo.pending ? 'Pending' : 'Uploaded'}
                  </div>
                ))}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-16 h-16 rounded-lg flex items-center justify-center border-2 border-dashed"
                  style={{ borderColor: colors.border, color: colors.textMuted }}
                >
                  <Camera size={24} />
                </button>
              </div>
            </Card>
          )}

          {/* Notes */}
          <Card className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add inspection notes..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl outline-none resize-none"
              style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
            />
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => handleSaveInspection(false)}
              disabled={saving}
              className="flex-1"
            >
              Save Draft
            </Button>
            <Button
              onClick={() => handleSaveInspection(true)}
              disabled={saving || completedItems < totalItems}
              className="flex-1"
            >
              {saving ? 'Saving...' : 'Complete'}
            </Button>
          </div>
        </div>

        {/* Notes review slideshow — one step's note + its photos per slide */}
        {notesOpen && <NotesSlideshow slides={noteSlides} onClose={() => setNotesOpen(false)} />}

        {lightbox && (
          <div
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-50 flex items-center justify-center p-5"
            style={{ background: 'rgba(0,0,0,0.9)' }}
          >
            <img src={lightbox} alt="" className="max-w-full max-h-full rounded-lg" />
          </div>
        )}
      </div>
    );
  }

  // New Inspection View
  if (view === 'new') {
    // Universe: only HGR-tagged units. Then filter by address (and unit id) search.
    const hgrUnits = units.filter(u => unitHasTag(u.tags, 'HGR'));
    const s = unitSearch.trim().toLowerCase();
    const visibleUnits = s
      ? hgrUnits.filter(u =>
          (u.address || '').toLowerCase().includes(s) ||
          String(u.id || '').toLowerCase().includes(s)
        )
      : hgrUnits;

    return (
      <div className="h-full overflow-y-auto pb-20" style={{ background: colors.black }}>
        <Header title="New Inspection" showBack onBack={() => setView('list')} />
        <div className="px-4">
          <Card className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>Select Unit</label>
            <div className="relative mb-3">
              <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: colors.textMuted }} />
              <input
                type="text"
                placeholder="Search by address..."
                value={unitSearch}
                onChange={e => setUnitSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl outline-none"
                style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              />
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto mb-4">
              {hgrUnits.length === 0 ? (
                <p className="text-sm text-center py-6" style={{ color: colors.textMuted }}>No HGR units found</p>
              ) : visibleUnits.length === 0 ? (
                <p className="text-sm text-center py-6" style={{ color: colors.textMuted }}>No units match "{unitSearch}"</p>
              ) : (
                visibleUnits.map(u => (
                  <button
                    key={u.id}
                    onClick={() => handleStartInspection(u.id, 'onboarding')}
                    disabled={saving}
                    className="w-full p-3 rounded-xl text-left disabled:opacity-50"
                    style={{ background: colors.inputBg, border: `2px solid transparent` }}
                  >
                    <p className="font-medium" style={{ color: colors.textPrimary }}>{u.address || 'No address'}</p>
                  </button>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // List View — the current tech's in-progress inspections sit above the
  // "Start New Inspection" button (resume these); completed ones go below it.
  const inProgress = myInspections.filter(i => i.status !== 'complete');
  const completed = myInspections.filter(i => i.status === 'complete');

  const resumeInspection = (insp) => {
    const def = INSPECTION_TYPES[insp.type];
    const items = insp.items || {};
    // Resume at the first unaddressed step (checklist only).
    const firstOpen = isChecklist(def)
      ? checklistSteps(def).findIndex(s => !stepAddressed(items[s.id]))
      : -1;
    setSelectedInspection(insp);
    setFormData(items);
    setNotes(insp.notes || '');
    setPhotos(insp.photos || []);
    setStepIndex(firstOpen === -1 ? 0 : firstOpen);
    setView('form');
  };

  const renderCard = (insp) => {
    const style = statusStyle[insp.status] || statusStyle.pending;
    const { done: completedCount, total: totalCount } = inspectionProgress(INSPECTION_TYPES[insp.type], insp.items || {});
    const isComplete = insp.status === 'complete';
    return (
      <button
        key={insp.id}
        onClick={() => { if (!isComplete) resumeInspection(insp); }}
        className="w-full rounded-2xl p-4 text-left"
        style={{ background: colors.cardBg, cursor: isComplete ? 'default' : 'pointer' }}
      >
        <div className="flex justify-between items-start mb-3">
          <div>
            <p className="font-semibold" style={{ color: colors.textPrimary }}>{insp.address || insp.property || `Unit ${insp.unitId}`}</p>
          </div>
          <span
            className="px-3 py-1 rounded-full text-xs font-semibold"
            style={{ background: style.bg, color: style.color }}
          >
            {insp.status === 'complete' ? 'Complete' : `${completedCount}/${totalCount}`}
          </span>
        </div>
        <div className="flex justify-between items-center pt-3 border-t" style={{ borderColor: colors.border }}>
          <div className="flex items-center gap-2">
            <ClipboardCheck size={16} style={{ color: colors.textMuted }} />
            <span className="text-sm" style={{ color: colors.textSecondary }}>
              {INSPECTION_TYPES[insp.type]?.label || insp.type}
            </span>
          </div>
          <span className="text-xs" style={{ color: colors.textMuted }}>
            {new Date(insp.createdAt?.seconds * 1000 || insp.createdAt).toLocaleDateString()}
          </span>
        </div>
      </button>
    );
  };

  return (
    <div className="h-full overflow-y-auto pb-20" style={{ background: colors.black }}>
      <Header title="Inspect" subtitle="PM & Fire inspections" />
      <div className="px-4">
        {/* In-progress — resume these. Scrolls (~2 cards visible) so the
            Start New Inspection button stays in reach. */}
        <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
          {inProgress.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardCheck size={48} className="mx-auto mb-4 opacity-30" style={{ color: colors.textMuted }} />
              <p style={{ color: colors.textMuted }}>No inspections in progress</p>
              <p className="text-sm" style={{ color: colors.textMuted }}>Start a new inspection below</p>
            </div>
          ) : (
            inProgress.map(renderCard)
          )}
        </div>

        <Button onClick={() => setView('new')} className="w-full mt-4">
          <Plus size={20} /> Start New Inspection
        </Button>

        {/* Completed — below the new-inspection button */}
        {completed.length > 0 && (
          <div className="mt-6">
            <p className="text-xs font-semibold uppercase tracking-wide mb-3 px-1" style={{ color: colors.textMuted }}>
              Completed
            </p>
            <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
              {completed.map(renderCard)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InspectScreen;
